import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router';
import {
  assistantFactsKind, assistantPageLabel, type AssistantObjectType, type AssistantPageContext, type AssistantPageFacts,
} from '@contracts/assistant';
import {
  AUDIT_MODULES, UUID_RE as UUID, VIA_CHANNELS, type AuditModule, type Id, type ViaChannel,
} from '@contracts/common';
import { DIRECTIONS, ENTRY_STATUSES, type Direction, type EntryStatusFilter } from '@contracts/finance';
import type { BoardDTO } from '@contracts/workplace';
import { parseDate } from '@domain/dates';
import { useWorkplaceFocus, type WorkplaceFocus } from '../workplace/focus';
import { BOARD_KEY } from '../workplace/useBoard';

/**
 * D120, D160: the page the person is looking at, as a turn sends it. The route is the path only; what the page shows
 * besides goes as typed facts parsed from the URL (and, on the Workplace, the focus its page published), never as the
 * query string: the Workplace search text never leaves the browser. The server re-checks every id (D76, D160); an id
 * is sent only in the shape its `z.uuid()` accepts (`UUID_RE`, R15), so an odd one is left out, never a 422.
 */
const YEAR = /^\d{4}$/;
const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;
const STATUSES: readonly string[] = [...ENTRY_STATUSES, 'open'];

/** R15: the server's `year` (2000–2100); anything else is left out, never refused (§6.1). */
function yearOf(value: string | null): number | undefined {
  if (value === null || !YEAR.test(value)) return undefined;
  const n = Number(value);
  return n >= 2000 && n <= 2100 ? n : undefined;
}
/** R15: a day the calendar has (`parseDate` throws on `2026-02-30`), as the server's `isoDate` checks. */
function dayOf(value: string | null): string | undefined {
  if (value === null) return undefined;
  try {
    return parseDate(value);
  } catch {
    return undefined;
  }
}
const OBJECT_PAGES: readonly { re: RegExp; type: AssistantObjectType }[] = [
  { re: /^\/projects\/([^/]+)/, type: 'project' },
  { re: /^\/finance\/projects\/([^/]+)/, type: 'project' },
  { re: /^\/partners\/([^/]+)/, type: 'partner' },
  { re: /^\/strategy\/objectives\/([^/]+)/, type: 'objective' },
];

/** Only the fields that are set; a variant with nothing but its kind is no facts at all. */
function factsOrNone(facts: AssistantPageFacts): AssistantPageFacts | null {
  const set = Object.entries(facts).filter(([, v]) => v !== undefined);
  return set.length <= 1 ? null : Object.fromEntries(set) as AssistantPageFacts;
}

/** The log's channel filter (D170, S2 D77): the assistant or an outside AI connection; anything else is dropped. */
const matchedVia = (raw: string | null): ViaChannel | undefined =>
  VIA_CHANNELS.find((channel) => channel === raw);

function factsOf(pathname: string, params: URLSearchParams, focus: WorkplaceFocus | null): AssistantPageFacts | null {
  const matched = (value: string | null, re: RegExp): string | undefined =>
    (value !== null && re.test(value) ? value : undefined);
  const year = yearOf(params.get('year'));
  switch (assistantFactsKind(pathname)) {
    case 'dashboard':
      return { kind: 'dashboard', mine: params.get('mine') === 'true' };
    case 'workplace': {
      if (focus === null) return null;
      const rcpt = focus.filters.rcpt?.slice('user:'.length) ?? null;
      return factsOrNone({ kind: 'workplace', view: focus.view, flags: [...focus.filters.f],
        recipientId: rcpt !== null && UUID.test(rcpt) ? rcpt : undefined,
        projectId: focus.filters.prj !== null && UUID.test(focus.filters.prj) ? focus.filters.prj : undefined,
        sharing: focus.filters.share ?? undefined,
        // D173: the tree's id and a search flag; the search text itself stays here.
        treeId: focus.tree !== null && UUID.test(focus.tree) ? focus.tree : undefined,
        searching: focus.searching ? true : undefined });
    }
    case 'strategy':
      return factsOrNone({ kind: 'strategy', year });
    case 'finance': {
      // D174: the entries page's scope, never its category.
      const direction = params.get('direction');
      const status = params.get('status');
      return factsOrNone({ kind: 'finance', year, month: matched(params.get('month'), MONTH),
        projectId: matched(params.get('projectId'), UUID),
        direction: direction !== null && (DIRECTIONS as readonly string[]).includes(direction) ? direction as Direction
          : undefined,
        status: status !== null && STATUSES.includes(status) ? status as EntryStatusFilter : undefined });
    }
    case 'activity': {
      const module = params.get('module');
      return factsOrNone({ kind: 'activity', from: dayOf(params.get('from')), to: dayOf(params.get('to')),
        actorId: matched(params.get('actorId'), UUID),
        module: module !== null && (AUDIT_MODULES as readonly string[]).includes(module) ? module as AuditModule : undefined,
        via: matchedVia(params.get('via')) });
    }
    default:
      return null;
  }
}

/** null = not a page the assistant knows (`assistantPageLabel` is null): the turn carries no page. */
export function pageContextOf(pathname: string, search: string,
  itemTypeOf: (id: Id) => 'task' | 'note' | null, focus: WorkplaceFocus | null = null): AssistantPageContext | null {
  if (assistantPageLabel(pathname) === null) return null;
  const onWorkplace = pathname === '/workplace';
  const facts = factsOf(pathname, new URLSearchParams(search), onWorkplace ? focus : null);
  const page: AssistantPageContext = { route: pathname, ...(facts === null ? {} : { facts }) };
  for (const { re, type } of OBJECT_PAGES) {
    const id = re.exec(pathname)?.[1];
    if (id !== undefined && UUID.test(id)) return { ...page, objectRef: { type, id } };
  }
  if (onWorkplace) {
    if (focus !== null && focus.selectedId !== null && focus.selectedType !== null && UUID.test(focus.selectedId)) {
      return { ...page, objectRef: { type: focus.selectedType, id: focus.selectedId } };
    }
    const id = new URLSearchParams(search).get('item');
    const type = id !== null && UUID.test(id) ? itemTypeOf(id) : null;
    if (id !== null && type !== null) return { ...page, objectRef: { type, id } };
  }
  return page;
}

/** `pageContextOf` over `useLocation()`, the board cache (`BOARD_KEY`, never fetched) and the Workplace focus. */
export function usePageContext(): AssistantPageContext | null {
  const { pathname, search } = useLocation();
  const queryClient = useQueryClient();
  const focus = useWorkplaceFocus();
  return pageContextOf(pathname, search, (id) =>
    queryClient.getQueryData<BoardDTO>(BOARD_KEY)?.items.find((item) => item.id === id)?.itemType ?? null, focus);
}
